import {
  ComponentMultiStyleConfig,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, { useCallback, useMemo } from "react";
import { ComponentStyleProps } from "../../../lib/styles/props/component-style-props";
import { clamp } from "lodash";
import { hasValue } from "../../../lib/util/common-util";

export type CMFormInputBaseNumberProps = {
  editMode?: "editable" | "disabled" | "readOnly";
  variant?: "default" | "error" | "diff";
  size?: "sm" | "md" | "lg";

  /**
   * 値。
   */
  value?: number | null;

  /**
   * Stepper選択時の加減値の設定
   * 未設定の場合:1
   */
  step?: number;

  /**
   * 小数点の設定(精度)
   * 未設定の場合:小数点なし
   */
  precision?: number;

  /**
   * 入力エリアの最小値。
   * 未設定の場合: 0
   */
  min?: number;

  /**
   * 入力エリアの最大値。
   * 未設定の場合: 9999
   */
  max?: number;

  /**
   * 差分モード時の変更前の値。
   * 差分モードの場合のみ必須。
   */
  changedFrom?: number | null;

  onChange?: (value: number | null) => void;
  onBlur?: () => void;
} & ComponentStyleProps;

/**
 * NumberInputコンポーネント.
 *
 * 初期値や小数点表示、Stepperの加減幅の設定が行える。
 * 非活性、読み取り専用モードの場合は、Stepper非表示.
 * スタイルはパーツごとに適用する必要がある。
 */
export const CMFormInputBaseNumber = ({
  editMode = "editable",
  variant = "default",
  size = "md",

  value = null,
  step = 1,
  precision,
  min = 0,
  max = 9999,

  changedFrom = null,

  onChange,
  onBlur,

  sx,
  ...rest
}: CMFormInputBaseNumberProps) => {
  const styles = useMultiStyleConfig("CMFormInputBaseNumberStyles", {
    variant: variant === "error" ? "error" : "default",
    size,
  });

  const isDiffMode = useMemo(() => variant === "diff", [variant]);

  const disabled = editMode === "disabled" || editMode === "readOnly";
  const isInvalid = variant === "error";

  const clampedValue = useMemo(
    () => (hasValue(value) ? clamp(value, min, max) : null),
    [value, min, max],
  );

  /**
   * NumberInputの入力値を取得し、呼び出し元に返却
   */
  const handleChange = useCallback(
    (_valueAsString: string, valueAsNumber: number) => {
      const value = Number.isNaN(valueAsNumber) ? null : valueAsNumber;
      onChange?.(value);
    },
    [onChange],
  );

  /**
   * 差分モード時、値が変更前と異なるならtrue。
   * 以下のいずれかの場合はfalse。
   * - 差分モードではない。
   * - 変更前と値が同一である。
   */
  const hasChangedInDiffMode = useMemo(
    () => isDiffMode && value !== changedFrom,
    [isDiffMode, value, changedFrom],
  );

  const inputStyles = useMemo(
    () => ({
      ...styles.containerBase,
      ...(hasChangedInDiffMode ? styles.diff : undefined),
      ...sx,
    }),
    [styles, hasChangedInDiffMode, sx],
  );
  const readonlyStyles = useMemo(
    () => ({
      ...styles.readonly,
      ...(hasChangedInDiffMode ? styles.diff : undefined),
      ...sx,
    }),
    [styles, hasChangedInDiffMode, sx],
  );

  return editMode === "readOnly" ? (
    <Text sx={readonlyStyles} {...rest}>
      {value}
    </Text>
  ) : (
    <NumberInput
      isInvalid={isInvalid}
      value={clampedValue ?? undefined}
      min={min}
      max={max}
      step={step}
      precision={precision}
      onChange={handleChange}
      onBlur={onBlur}
    >
      <NumberInputField disabled={disabled} sx={inputStyles} {...rest} />
      {disabled ? null : (
        <NumberInputStepper sx={styles.stepperGroupBase}>
          {/*加算用*/}
          <NumberIncrementStepper sx={styles.stepper} />
          {/*減算用*/}
          <NumberDecrementStepper sx={styles.stepper} />
        </NumberInputStepper>
      )}
    </NumberInput>
  );
};

export const CMFormInputBaseNumberStyles: ComponentMultiStyleConfig = {
  parts: ["containerBase", "stepperGroupBase", "stepper"],
  baseStyle: {
    containerBase: {
      bgColor: "white",
    },
    readonly: {
      maxW: "max-content",
    },
    diff: {
      borderWidth: "1px",
      borderColor: "orange.500",
      _hover: {
        borderColor: "orange.500",
      },
      _focus: {
        _hover: {
          borderColor: "blue.500",
        },
      },
    },
  },
  variants: {
    default: {
      containerBase: {},
    },
    error: {
      containerBase: {},
    },
    // diff: {
    //   containerBase: {
    //     borderWidth: "1px",
    //     borderColor: "orange.500",
    //     _hover: {
    //       borderColor: "orange.500",
    //     },
    //     _focus: {
    //       _hover: {
    //         borderColor: "blue.500",
    //       },
    //     },
    //   },
    //   containerReadOnly: {},
    // },
  },
  sizes: {
    sm: {
      containerBase: {
        h: "22px",
        px: "6px",
      },
      stepperGroupBase: {
        h: "20px",
      },
      stepper: {
        "font-size": "0.6rem",
      },
    },
    md: {
      containerBase: {
        h: "30px",
        px: "8px",
      },
      stepperGroupBase: {
        h: "28px",
      },
      stepper: {},
    },
    lg: {
      containerBase: {
        h: "38px",
        px: "10px",
      },
      stepperGroupBase: {
        h: "36px",
      },
      stepper: {},
    },
  },
};
